/* ReloginModal — re-run a streamed login for an already-connected account.
 *
 * Two phases: first choose what proxy to log in through — keep the current one
 * (no change), auto-provision a new pool proxy, or enter one manually — then run
 * LoginStream. "Keep current" touches nothing server-side; the other two re-source
 * via change-proxy (OnlyFans/Fansly bind the session to an IP, so changing the
 * proxy requires a fresh login, which is exactly what this modal then does). */
import { useState } from 'react';
import { ProxyPicker } from './ProxyPicker';
import { LoginStream } from './LoginStream';
import { C } from './onboardingKit';
import { normalizePlatform, type Platform } from '../PlatformLogo';

interface ReloginModalProps {
  accountId: string;
  platform?: Platform;
  onClose: () => void;
  onConnected?: () => void;
}

const LABEL: Record<'onlyfans' | 'fansly', string> = { onlyfans: 'OnlyFans', fansly: 'Fansly' };

export function ReloginModal({ accountId, platform, onClose, onConnected }: ReloginModalProps) {
  const [phase, setPhase] = useState<'proxy' | 'login'>('proxy');
  const label = LABEL[normalizePlatform(platform)];
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: phase === 'login' ? 836 : 520, maxWidth: '100%', maxHeight: 'calc(92 * var(--om-vh))', overflowY: 'auto',
          background: 'var(--om-card)', border: `1px solid ${C.line2}`, borderRadius: 18, padding: 26,
          boxShadow: '0 24px 70px rgba(0,0,0,.6)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ color: C.text, fontSize: 20, fontWeight: 800, margin: 0 }}>
            {phase === 'proxy' ? 'Re-login' : `Re-login to ${label}`}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.5, margin: '0 0 18px' }}>
          {phase === 'proxy'
            ? 'Choose which proxy to log in through, then sign in again to refresh this account’s session.'
            : 'Sign in again below. Press Done once you’re on your account.'}
        </p>
        {phase === 'proxy' ? (
          <ProxyPicker
            accountId={accountId}
            variant="change"
            allowKeepCurrent
            onSourced={() => setPhase('login')}
            onCancel={onClose}
          />
        ) : (
          <LoginStream
            accountId={accountId}
            onConnected={() => {
              onConnected?.();
              onClose();
            }}
            onStopped={onClose}
          />
        )}
      </div>
    </div>
  );
}
